// User types
export type UserRole = 
  | 'super_admin' 
  | 'admin' 
  | 'store_manager' 
  | 'sales_staff' 
  | 'rider'       // Nairobi local deliveries
  | 'driver'      // Long-distance trucking
  | 'supplier'    // External suppliers who can book transport
  | 'consumer'    // External consumers who can order deliveries
  | 'sales'       // Legacy role
  | 'regional_manager'; // Legacy role

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  storeId?: string; // Links user to a specific store (null for super_admin)
  phone?: string;
  createdAt: Date;
  lastActive?: Date;
  vehicleId?: string; // For rider/driver: their assigned vehicle
  licenseNumber?: string; // For driver: license info
}

// ====== MULTI-STORE ERP SYSTEM (v3.0) ======

// Store Management
export interface Store {
  id: string;
  name: string; // e.g., "Gikomba Branch", "Nakuru CBD"
  location: {
    lat: number;
    lng: number;
    address: string;
  };
  managerId: string; // The user ID of the manager
  status: 'active' | 'renovating' | 'closed';
  targetRevenue: number; // Monthly target
  createdAt: Date;
}

// Operational Requests from Store Managers
export type RequestType = 'restock_order' | 'maintenance' | 'equipment' | 'funds';
export type RequestStatus = 'pending' | 'approved' | 'rejected' | 'in_progress' | 'completed';
export type PriorityLevel = 'low' | 'medium' | 'high' | 'critical';

export interface StoreRequest {
  id: string;
  storeId: string;
  storeName: string;
  requesterId: string; // Manager who asked
  requesterName: string;
  type: RequestType;
  title: string;
  description: string;
  estimatedCost?: number;
  priority: PriorityLevel;
  status: RequestStatus;
  adminNotes?: string; // Notes from HQ
  createdAt: Date;
  updatedAt: Date;
}

// Inventory Balancing between Stores
export interface InventoryTransfer {
  id: string;
  fromStoreId: string;
  fromStoreName: string;
  toStoreId: string;
  toStoreName: string;
  batchId: string; // The specific batch of onions moving
  quantityKg: number;
  onionType: OnionType;
  onionSize: OnionSize;
  status: 'pending_approval' | 'in_transit' | 'completed' | 'rejected';
  requestDate: Date;
  completionDate?: Date;
  approvedBy?: string; // Super Admin ID
  reason: string;
}

// Mystery Shopper & Quality Audits
export interface StoreAudit {
  id: string;
  storeId: string;
  storeName: string;
  auditorId: string; // Could be you or a secret shopper account
  auditorName: string;
  date: Date;
  metrics: {
    cleanliness: number; // 1-5
    staffAttitude: number; // 1-5
    stockDisplay: number; // 1-5
    checkoutSpeed: number; // 1-5
  };
  photos?: string[]; // URLs to photos of the store state
  notes: string;
  overallScore: number; // Average of metrics
  createdAt: Date;
}

// Sale types
export type OnionSize = 'Small' | 'Medium' | 'Large' | 'Mixed';
export type OnionType = 'Red' | 'White' | 'Yellow' | 'Mixed';
export type PaymentMethod = 'Cash' | 'M-Pesa' | 'Bank' | 'Credit';
export type PaymentStatus = 'Paid' | 'Partial' | 'Credit';

export interface Sale {
  id: string;
  storeId?: string; // Multi-store support
  clientId: string;
  clientName: string;
  clientPhone: string;
  clientLocation: string;
  onionSize: OnionSize;
  onionType: OnionType;
  pricePerKg: number;
  quantityKg: number;
  totalValue: number;
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  amountPaid?: number;
  amountOwed?: number;
  saleDate: Date;
  salesperson?: string;
  notes?: string;
  location?: {
    lat: number;
    lng: number;
    accuracy: number; // meters
  };
  linkedOrderId?: string; // set when auto-created from a delivered CustomerOrder
  createdAt: Date;
  createdBy: string;
}

// Client types
export type RiskFlag = 'Inactive' | 'Regular' | 'High-value' | 'At-risk';
export type BuyerFrequency = 'Weekly' | 'Bi-weekly' | 'Monthly' | 'Irregular' | 'New';

export interface Client {
  id: string;
  name: string;
  phone: string;
  location: string;
  
  // Aggregated metrics
  totalPurchases: number; // total number of purchases
  totalPurchaseKg: number; // total kg bought
  totalPurchaseValue: number; // total money spent
  averageOrderSizeKg: number;
  averageOrderValue: number;
  averagePricePerKg: number;
  
  // Frequency analysis
  purchaseFrequency: BuyerFrequency;
  averageDaysBetweenPurchases: number;
  lastPurchaseDate: Date;
  secondLastPurchaseDate?: Date;
  predictedNextPurchase?: Date;
  daysSinceLastPurchase: number;
  daysUntilNextPurchase?: number;
  isOverdue: boolean;
  
  // Risk & recommendations
  riskFlag: RiskFlag;
  buyingProbabilityScore: number; // 0-100
  callRecommendation?: string;
  shouldCallToday: boolean;
  
  // Preferences (learned from history)
  preferredOnionSize?: OnionSize;
  preferredOnionType?: OnionType;
  preferredPaymentMethod?: PaymentMethod;
  
  // Metadata
  firstPurchaseDate: Date;
  lastUpdated: Date;
  createdAt: Date;
}

// Expense types
export type ExpenseCategory = 
  | 'Transport'
  | 'Labor'
  | 'Packaging'
  | 'Storage'
  | 'Losses'
  | 'Market fees'
  | 'Utilities'
  | 'Rent'
  | 'Miscellaneous'
  | string; // Allow custom categories

export interface Expense {
  id: string;
  date: Date;
  category: ExpenseCategory;
  description: string;
  amount: number;
  paymentMethod: PaymentMethod;
  linkedSaleId?: string;
  linkedBatchId?: string;
  createdAt: Date;
  createdBy: string;
}

// ── Genvitungu Plus membership (monthly: free delivery + locked price) ───────
export type MembershipPlan = 'plus';
export type MembershipStatus = 'pending' | 'active' | 'expired';

export interface Membership {
  id: string;        // = sanitized phone digits (lookup key)
  phone: string;     // formatted (+254…)
  name: string;
  plan: MembershipPlan;
  status: MembershipStatus;
  mpesaCode?: string;     // M-Pesa confirmation the customer paid the monthly fee
  startedAt?: Date;
  expiresAt?: Date;       // null/undefined = no expiry tracked yet
  createdAt: Date;
  updatedAt: Date;
}

// ── B2B supply plans (recurring standing orders) ─────────────────────────────
export type SupplyFrequency = 'weekly' | 'biweekly' | 'monthly';
export type SupplyPlanStatus = 'pending' | 'active' | 'paused' | 'cancelled';

export interface SupplyPlanItem {
  onionType: OnionType;
  onionSize: OnionSize;
  quantityKg: number;
}

export interface SupplyPlan {
  id: string;
  businessName: string;
  contactName: string;
  phone: string;
  email?: string;
  deliveryLocation: string;
  estate?: string;
  items: SupplyPlanItem[];
  frequency: SupplyFrequency;
  pricePerKg?: number;        // agreed B2B rate, set by staff on activation
  nextDeliveryDate?: Date;    // when the next standing order should be generated
  status: SupplyPlanStatus;
  notes?: string;
  lastOrderCode?: string;     // tracking code of the most recent generated order
  createdAt: Date;
  updatedAt: Date;
}

export const SUPPLY_FREQUENCY_DAYS: Record<SupplyFrequency, number> = {
  weekly: 7,
  biweekly: 14,
  monthly: 30,
};

// ── Post-delivery quality claims (replace / refund) ──────────────────────────
export type QualityIssueType = 'bruised' | 'overripe' | 'wrong_grade' | 'damaged' | 'short_weight' | 'other';
export type ResolutionMethod = 'replacement' | 'refund' | 'either';
export type QualityClaimStatus = 'pending' | 'approved_replacement' | 'approved_refund' | 'rejected' | 'resolved';

export const QUALITY_ISSUE_LABELS: Record<QualityIssueType, string> = {
  bruised: 'Bruised / soft',
  overripe: 'Overripe / sprouting',
  wrong_grade: 'Wrong grade or size',
  damaged: 'Damaged in transit',
  short_weight: 'Short weight',
  other: 'Other',
};

export const QUALITY_CLAIM_LABELS: Record<QualityClaimStatus, string> = {
  pending: 'Under review',
  approved_replacement: 'Replacement approved',
  approved_refund: 'Refund approved',
  rejected: 'Not approved',
  resolved: 'Resolved',
};

export interface QualityClaim {
  id: string;
  orderId: string;
  trackingCode: string;
  customerName: string;
  customerPhone: string;
  issueType: QualityIssueType;
  description: string;
  photoUrls?: string[]; // optional evidence photos (Firebase Storage)
  preferredResolution: ResolutionMethod;
  status: QualityClaimStatus;
  adminNotes?: string;
  resolvedBy?: string;
  resolvedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// Actual cost of fulfilling a specific delivery (logged when it exceeds, or to track against, the fee charged)
export interface DeliveryExpense {
  id: string;
  orderId: string;
  trackingCode: string;
  driverName?: string;
  actualCost: number; // KSh spent on this delivery
  reason: string;     // e.g. "fuel", "rider pay", "long distance"
  date: Date;
  createdAt: Date;
  createdBy: string;
}

// Inventory types
export type StockMovementType = 'Purchase' | 'Sale' | 'Spoilage' | 'Loss' | 'Adjustment';

export interface InventoryBatch {
  id: string;
  storeId?: string; // Multi-store support
  batchDate: Date;
  supplier: string;
  onionSize: OnionSize;
  onionType: OnionType;
  initialStockKg: number;
  currentStockKg: number;
  costPerKg: number;
  totalCost: number;
  spoilageKg: number;
  soldKg: number;
  notes?: string;
  createdAt: Date;
  createdBy: string;
}

export interface StockMovement {
  id: string;
  date: Date;
  type: StockMovementType;
  batchId?: string;
  saleId?: string;
  quantityKg: number;
  onionSize: OnionSize;
  onionType: OnionType;
  reason?: string;
  createdAt: Date;
  createdBy: string;
}

export interface InventorySummary {
  totalStockKg: number;
  totalStockValue: number;
  averageCostPerKg: number;
  spoilageRate: number;
  daysOfStockRemaining: number;
  lowStockAlert: boolean;
  bySize: Record<OnionSize, number>;
  byType: Record<OnionType, number>;
}

// Analytics types
export interface SalesSummary {
  totalRevenue: number;
  totalKgSold: number;
  totalSales: number;
  averageSaleValue: number;
  averagePricePerKg: number;
  cashSales: number;
  creditSales: number;
  outstandingCredit: number;
}

export interface FinancialSummary {
  revenue: number;
  expenses: number;
  grossProfit: number;
  netProfit: number;
  profitMargin: number;
  profitPerKg: number;
  cashInHand: number;
  outstandingCredit: number;
}

export interface ClientInsight {
  client: Client;
  buyingProbability: number;
  recommendedAction: string;
  talkingPoints: string[];
}

// Call tracking
export type CallOutcome = 'Called' | 'Interested' | 'Not reachable' | 'Order confirmed' | 'Not interested';

export interface CallLog {
  id: string;
  clientId: string;
  clientName: string;
  date: Date;
  outcome: CallOutcome;
  notes?: string;
  followUpDate?: Date;
  orderConfirmed?: boolean;
  expectedOrderKg?: number;
  createdBy: string;
}

// Dashboard types
export interface DashboardData {
  period: 'today' | 'week' | 'month';
  sales: SalesSummary;
  financial: FinancialSummary;
  inventory: InventorySummary;
  topClients: Client[];
  upcomingBuyers: ClientInsight[];
  lostClients: Client[];
}

// Date range for filtering
export interface DateRange {
  start: Date;
  end: Date;
}

// ====== ENTERPRISE FEATURES (v2.0+) ======

// Supplier Management (Phase 1.3)
export interface Supplier {
  id: string;
  name: string;
  phone: string;
  location: string;
  email?: string;
  rating: number; // 1-5 stars
  averagePrice: number;
  totalPurchases: number;
  totalPurchaseValue: number;
  spoilageRate: number; // percentage
  lastPurchaseDate?: Date;
  notes?: string;
  isActive: boolean;
  createdAt: Date;
  createdBy: string;
}

// Payment Tracking (Phase 1.4)
export type PaymentType = 'Full Payment' | 'Partial Payment' | 'Down Payment';
export type PaymentSource = 'Cash' | 'M-Pesa' | 'Bank Transfer' | 'Cheque';

export interface Payment {
  id: string;
  saleId: string;
  clientId: string;
  clientName: string;
  amount: number;
  paymentType: PaymentType;
  paymentSource: PaymentSource;
  paymentDate: Date;
  transactionRef?: string; // M-Pesa code, cheque number, etc.
  notes?: string;
  createdAt: Date;
  createdBy: string;
}

// Credit & Debt Management (Phase 2.1)
export type CreditStatus = 'active' | 'warning' | 'frozen';
export type ReminderType = 'polite' | 'firm' | 'final';

export interface ClientCredit {
  clientId: string;
  creditLimit: number;
  currentDebt: number;
  availableCredit: number;
  creditScore: number; // 0-100
  paymentStreak: number; // consecutive on-time payments
  lastScoreUpdate: Date;
  status: CreditStatus;
  frozenReason?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface PaymentReminder {
  id: string;
  clientId: string;
  clientName: string;
  saleId: string;
  amount: number;
  daysOverdue: number;
  reminderType: ReminderType;
  message: string;
  sentAt: Date;
  responseReceived: boolean;
  responseNotes?: string;
  createdBy: string;
}

// Smart Rot Inventory (Phase 2.2)
export type BathGrade = 'A' | 'B' | 'C'; // Premium, Standard, Clearance
export type SpoilageRisk = 'low' | 'medium' | 'high' | 'critical';

export interface GradeChange {
  date: Date;
  fromGrade: BathGrade;
  toGrade: BathGrade;
  reason: string;
  valueLoss: number;
  performedBy: string;
}

export interface EnhancedInventoryBatch extends Omit<InventoryBatch, 'supplier'> {
  supplierId?: string;
  supplierName: string;
  grade: BathGrade;
  ageInDays: number; // calculated field
  spoilageRisk: SpoilageRisk;
  recommendedAction?: string;
  gradeHistory: GradeChange[];
}

// Alerts
export type AlertType = 'spoilage' | 'credit_overdue' | 'low_stock' | 'high_value_client';
export type AlertPriority = 'low' | 'medium' | 'high' | 'critical';

export interface Alert {
  id: string;
  type: AlertType;
  priority: AlertPriority;
  title: string;
  message: string;
  actionUrl?: string;
  relatedId?: string; // batchId, clientId, saleId
  isRead: boolean;
  createdAt: Date;
  expiresAt?: Date;
}

// Analytics Extensions
export interface SupplierPerformance {
  supplier: Supplier;
  avgSpoilageRate: number;
  avgProfitMargin: number;
  reliability: number; // 0-100
  costEfficiency: number; // compared to market average
  recommendation: 'excellent' | 'good' | 'fair' | 'poor';
}

export interface CashFlowSummary {
  totalSales: number;
  totalPaymentsReceived: number;
  pendingPayments: number;
  overduePayments: number;
  cashOnHand: number;
  projectedRevenue: number; // based on pending payments
}

export interface ProfitAnalysis {
  totalRevenue: number;
  costOfGoods: number;
  grossProfit: number;
  expenses: number;
  netProfit: number;
  profitMarginPercent: number;
  spoilageLoss: number;
  bestPerformingProduct: {
    type: OnionType;
    size: OnionSize;
    profitMargin: number;
  };
}

// ====== AUDIT TRAIL (v4.0) ======
export type AuditCollection = 'sales' | 'inventory' | 'expenses' | 'clients' | 'inventory_batches' | 'payments' | 'suppliers';
export type AuditAction = 'create' | 'update' | 'delete';

export interface AuditLogEntry {
  id: string;
  collection: AuditCollection;
  documentId: string;
  documentLabel: string; // Human-readable identifier e.g. "Sale to John Kamau - KSh 5,000"
  action: AuditAction;
  changedBy: string; // User ID
  changedByName: string;
  changedByRole: UserRole;
  storeId?: string;
  before: Record<string, any>; // Snapshot before change
  after: Record<string, any>;  // Snapshot after change
  changedFields: string[];     // List of field names that changed
  reason?: string;             // Optional reason for the change
  createdAt: Date;
}

// ====== CUSTOMER ORDERS (v4.0) ======
export type OrderStatus = 'pending' | 'confirmed' | 'preparing' | 'out_for_delivery' | 'delivered' | 'cancelled';
export type OrderPaymentStatus = 'pending' | 'paid';
export type OrderPaymentMethod = 'pay_on_delivery' | 'pay_before';

export interface CustomerOrder {
  id: string;
  // Customer info
  customerName: string;
  customerPhone: string;
  customerAltPhone?: string;
  customerEmail?: string; // optional — used for confirmation + status emails
  // Delivery location
  deliveryLocation: string;
  houseNumber: string; // e.g. "A12" — required for delivery
  estate: string;      // e.g. "Kilimani Highrise" — required for delivery
  deliveryCoords?: {
    lat: number;
    lng: number;
  };
  deliveryInstructions?: string;
  // Order items
  items: OrderItem[];
  totalWeightKg: number;
  estimatedTotal?: number; // Goods subtotal based on current prices (excludes delivery)
  pricePerKgSnapshot?: number; // Tier rate applied at order time
  // Delivery fee (free inside Buruburu, else rate × km from Buruburu)
  deliveryDistanceKm?: number;
  deliveryFee?: number;
  discount?: number;                  // Plus member per-order discount (KSh)
  // Payment
  paymentMethod?: OrderPaymentMethod; // how the customer chose to pay
  mpesaTillNumber?: string;           // till shown for pay-before orders
  mpesaConfirmationCode?: string;     // M-Pesa code entered by customer (pay-before)
  // Status tracking
  status: OrderStatus;
  paymentStatus: OrderPaymentStatus;
  saleId?: string; // set once a Sale has been auto-created on delivery (idempotency guard)
  reorderReminderSentAt?: Date;      // set when a reorder reminder email has been sent
  qualityClaimId?: string;           // set when the customer reports a quality issue
  qualityClaimStatus?: QualityClaimStatus; // mirrored onto the order for consumer visibility
  // Assignment
  assignedStoreId?: string;
  assignedStoreName?: string;
  assignedDriverId?: string;
  assignedDriverName?: string;
  // Tracking
  trackingCode: string; // Short code for customer to track e.g. "ORD-839264"
  estimatedDeliveryTime?: string;
  // Notification audit (written by Cloud Functions)
  emailsSent?: {
    admin?: Date;
    adminRecipients?: string[];
    customer?: Date;
    statusNotified?: OrderStatus;
    statusNotifiedAt?: Date;
    adminError?: string;
    customerError?: string;
    statusError?: string;
  };
  // Admin notes
  adminNotes?: string;
  cancellationReason?: string;
  // Timestamps
  createdAt: Date;
  confirmedAt?: Date;
  deliveredAt?: Date;
  updatedAt: Date;
}

export interface OrderItem {
  onionType: OnionType;
  onionSize: OnionSize;
  quantityKg: number;
  pricePerKg?: number; // Filled by admin when confirmed
  subtotal?: number;
}

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  pending: 'Pending Review',
  confirmed: 'Order Confirmed',
  preparing: 'Preparing Stock',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  preparing: 'bg-purple-100 text-purple-800',
  out_for_delivery: 'bg-orange-100 text-orange-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

// ====== LOGISTICS & FLEET MANAGEMENT (v5.0) ======

// Fleet Management Types
export type VehicleType = 'truck' | 'motorcycle';
export type VehicleStatus = 'active' | 'maintenance' | 'off_duty';

export interface Vehicle {
  id: string;
  type: VehicleType;
  registrationPlate: string;
  capacityKg: number;
  status: VehicleStatus;
  driverId?: string; // Currently assigned driver/rider
  driverName?: string;
  currentLocation?: { 
    lat: number; 
    lng: number; 
    lastUpdated: Date;
  };
  performanceMetrics: {
    totalTrips: number;
    totalDistanceKm: number;
    fuelEfficiencyScore: number; // 0-100 (Sal calculates this!)
    averageDeliveryTime: number; // in minutes
    onTimeDeliveryRate: number; // percentage
  };
  fuelType?: string; // e.g., "Diesel", "Petrol"
  yearOfManufacture?: number;
  lastMaintenanceDate?: Date;
  nextMaintenanceDate?: Date;
  createdAt: Date;
  createdBy: string;
}

// Shipment / Logistics Tracking
export type ShipmentStatus = 'pending' | 'assigned' | 'in_transit' | 'delivered' | 'cancelled';
export type RequesterType = 'consumer' | 'supplier' | 'internal';

export interface TrackingUpdate {
  timestamp: Date;
  location: { lat: number; lng: number };
  note: string;
  updatedBy?: string;
}

export interface Shipment {
  id: string;
  trackingCode: string; // e.g., "SHIP-3492" for easy customer lookup
  
  // Requester info
  requesterId: string; // Consumer, Supplier, or Store Manager
  requesterName: string;
  requesterPhone?: string;
  requesterType: RequesterType;
  
  // Pickup & Delivery
  pickupLocation: { lat: number; lng: number; address: string };
  dropoffLocation: { lat: number; lng: number; address: string };
  
  // Cargo details
  cargoWeightKg: number;
  cargoDescription?: string;
  vehiclePreference: VehicleType; // truck or motorcycle
  
  // Assignment
  status: ShipmentStatus;
  assignedVehicleId?: string;
  assignedVehiclePlate?: string;
  assignedDriverId?: string;
  assignedDriverName?: string;
  
  // Tracking
  trackingUpdates: TrackingUpdate[];
  estimatedDeliveryTime?: string;
  actualDeliveryTime?: Date;
  
  // Payment & Pricing
  deliveryFee?: number;
  paymentStatus?: 'pending' | 'paid';
  
  // Ratings (filled after delivery)
  driverRating?: number; // 1-5
  driverFeedback?: string;
  goodsRating?: number; // 1-5 (quality of delivered goods)
  goodsFeedback?: string;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

// Delivery Rating (after completion)
export interface DeliveryRating {
  id: string;
  shipmentId: string;
  trackingCode: string;
  customerId: string;
  customerName: string;
  
  // Driver/Rider Rating
  driverRating: number; // 1-5
  driverFeedback?: string;
  
  // Goods Quality Rating (for our system only)
  goodsRating?: number; // 1-5
  goodsFeedback?: string;
  
  // Timestamps
  createdAt: Date;
}

// Driver Wallet & Payouts (for future implementation)
export interface DriverWallet {
  userId: string;
  driverName: string;
  balance: number;
  totalEarnings: number;
  totalWithdrawals: number;
  completedDeliveries: number;
  pendingDeliveries: number;
  lastPayoutDate?: Date;
  mpesaNumber?: string;
  updatedAt: Date;
}

export interface DriverPayout {
  id: string;
  driverId: string;
  driverName: string;
  amount: number;
  paymentMethod: 'mpesa' | 'bank' | 'cash';
  paymentReference?: string;
  status: 'pending' | 'completed' | 'failed';
  processedAt?: Date;
  createdAt: Date;
}

// ─────────────────────────────────────────────────────────────────────────────
// STOCK + INVENTORY TRACKER (per-product live stock-on-hand)
// Distinct from `inventory` / `inventory_batches` (procurement batch costing).
// Models sellable stock per onion variety/size. Stock is mutated ONLY by the
// inventory callables (recordRestock / recordAdjustment) and the order-delivered
// trigger — all via the admin SDK — so the numbers can't be client-forged.
// ─────────────────────────────────────────────────────────────────────────────
export type InventoryMovementType = 'restock' | 'sale' | 'adjustment' | 'transfer';
export type StockAlert = 'red' | 'amber' | 'green';

export interface Product {
  id: string;                  // slug, e.g. "red_medium"
  name: string;                // display name, e.g. "Red · Medium"
  onionType: OnionType;
  onionSize: OnionSize;
  currentStockKg: number;
  reorderThresholdKg: number;  // planning floor (kg)
  targetStockKg: number;       // restock-to target (kg)
  active: boolean;
  updatedAt?: Date;
}

export interface InventoryMovement {
  id: string;
  productId: string;
  type: InventoryMovementType;
  deltaKg: number;             // signed: + adds stock, − removes it
  reason?: string;             // adjustment/transfer note (NEVER customer PII)
  source?: string;             // restock source/supplier label
  valueKsh?: number;           // sale value (sum of item subtotals)
  orderRef?: string;           // order document id for `sale` movements
  by: string;                  // actor uid, or 'system' for the trigger
  at: number;                  // epoch millis
}

// View-model returned by the getInventory callable (Timestamps flattened to
// millis / 'YYYY-MM-DD' strings so the payload is plain JSON).
export interface ProductStockRow {
  id: string;
  name: string;
  onionType: OnionType;
  onionSize: OnionSize;
  currentStockKg: number;
  reservedKg: number;          // pending-order demand
  availableKg: number;         // currentStock − reserved
  reorderThresholdKg: number;
  targetStockKg: number;
  avgDailySoldKg: number;      // 7-day rolling average
  daysRemaining: number | null;
  alert: StockAlert;
  projectedStockoutDate: string | null;
  soldKg7d: number;
  revenue7d: number;
}

export interface InventoryChartPoint { date: string; stockKg: number; }

export interface InventoryDashboard {
  products: ProductStockRow[];
  movements: InventoryMovement[];
  chart: Record<string, InventoryChartPoint[]>;  // productId → 30-day stock series
  velocity: {
    totalSold7dKg: number;
    totalRevenue7d: number;
    topSellers: { id: string; name: string; soldKg: number }[];
    slowMovers: { id: string; name: string; soldKg: number }[];
  };
  generatedAt: number;
}

// Sal's AI Insights for Logistics
export interface SalLogisticsInsight {
  id: string;
  type: 'route_optimization' | 'fleet_efficiency' | 'predictive_maintenance' | 'demand_forecast';
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  actionable: boolean;
  suggestedAction?: string;
  relatedVehicleIds?: string[];
  relatedShipmentIds?: string[];
  createdAt: Date;
  dismissed: boolean;
}
